/**
 * Family members — PRD 5.7, same editable-config pattern as categories.
 *
 * This table is also the app's guest list: the email here is what Cloudflare
 * Access matches a signed-in person against, and the role is what opens Money,
 * House and Settings. So adding a member here is what lets Brian's kids in once
 * their Access accounts exist — no code change, no deploy.
 *
 * The Worker refuses to let you demote or remove yourself, or to strip the last
 * adult who can sign in. The UI says so up front instead of letting you find out
 * by getting an error.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FamilyMember, FamilyMemberInput, Role } from '../../shared/types';
import { ApiRequestError, api } from '../../lib/api';
import { useIdentity } from '../../lib/identity';
import { ConfigTable } from './ConfigTable';
import { ColorField, isHex } from './ColorField';
import { AlertNote, Button, Dialog, Field, Pill, Select, TextInput } from './ui';

const DEFAULT_COLOR = '#5b6ea8';

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) return err.message || fallback;
  return fallback;
}

export function FamilyPanel({ onStatus }: { onStatus: (message: string) => void }) {
  const me = useIdentity();
  const [items, setItems] = useState<FamilyMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Same split as CategoryPanel: the panel owns errors from panel actions, and a
  // dialog owns its own. Every refusal this router can hand back (email_taken,
  // self_email_change, last_adult) arrives while a dialog is open.
  const [actionError, setActionError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<FamilyMember | 'new' | null>(null);
  const [confirming, setConfirming] = useState<FamilyMember | null>(null);
  const [sharing, setSharing] = useState<FamilyMember | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function openEditor(member: FamilyMember | 'new') {
    setFormError(null);
    setEditing(member);
  }

  function closeEditor() {
    setFormError(null);
    setEditing(null);
  }

  function openConfirm(member: FamilyMember) {
    setConfirmError(null);
    setConfirming(member);
  }

  function closeConfirm() {
    setConfirmError(null);
    setConfirming(null);
  }

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await api.get<FamilyMember[]>(
        `/family${showInactive ? '?include_inactive=1' : ''}`
      );
      setItems(rows);
    } catch (err) {
      setItems(null);
      setError(errorText(err, "Couldn't load your family."));
    }
  }, [showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  /** A refusal keeps the form open with the reason inside it — never a silent no-op. */
  async function save(input: FamilyMemberInput) {
    setBusy(true);
    setFormError(null);
    try {
      if (editing && editing !== 'new') {
        await api.put<FamilyMember>(`/family/${editing.id}`, input);
        onStatus(`Saved ${input.name}.`);
      } else {
        await api.post<FamilyMember>('/family', input);
        onStatus(
          input.email
            ? `${input.name} added. They can sign in with ${input.email}.`
            : `${input.name} added. Add their email when they have an Access account.`
        );
      }
      closeEditor();
      await load();
    } catch (err) {
      setFormError(errorText(err, "Couldn't save that person."));
    } finally {
      setBusy(false);
    }
  }

  /** Restore comes from a row (panel error); remove comes from the confirm dialog (dialog error). */
  async function setActive(member: FamilyMember, active: boolean) {
    setBusy(true);
    if (active) setActionError(null);
    else setConfirmError(null);

    try {
      if (active) {
        await api.put<FamilyMember>(`/family/${member.id}`, { active: true });
        onStatus(`${member.name} is back in the household.`);
      } else {
        await api.del<FamilyMember>(`/family/${member.id}`);
        onStatus(`${member.name} removed. Their chores and allowance history are kept.`);
        closeConfirm();
      }
      await load();
    } catch (err) {
      const message = errorText(err, "Couldn't update that person.");
      if (active) setActionError(message);
      else setConfirmError(message);
    } finally {
      setBusy(false);
    }
  }

  function openSharing(member: FamilyMember) {
    setShareError(null);
    setSharing(member);
  }

  function closeSharing() {
    setShareError(null);
    setSharing(null);
  }

  /** Turn on sharing (or rotate the token). The fresh member carries the new token. */
  async function generateToken(member: FamilyMember) {
    setBusy(true);
    setShareError(null);
    try {
      const updated = await api.post<FamilyMember>(`/family/${member.id}/location-token`);
      setSharing(updated);
      onStatus(`Location sharing is on for ${member.name}.`);
      await load();
    } catch (err) {
      setShareError(errorText(err, "Couldn't turn on sharing."));
    } finally {
      setBusy(false);
    }
  }

  /** Turn off sharing. The old token stops working the moment this returns. */
  async function stopToken(member: FamilyMember) {
    setBusy(true);
    setShareError(null);
    try {
      const updated = await api.del<FamilyMember>(`/family/${member.id}/location-token`);
      setSharing(updated);
      onStatus(`Location sharing is off for ${member.name}.`);
      await load();
    } catch (err) {
      setShareError(errorText(err, "Couldn't turn off sharing."));
    } finally {
      setBusy(false);
    }
  }

  const isSelf = (member: FamilyMember) => me.memberId === member.id;

  return (
    <section aria-labelledby="family-heading" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="family-heading" className="font-display text-xl font-semibold text-ink">
            Family
          </h2>
          <p className="mt-1 max-w-prose text-sm text-ink-soft">
            Who's in the house, what they can see, and the color that stands for them across chores,
            the calendar and the map.
          </p>
        </div>
        <Button variant="primary" onClick={() => openEditor('new')}>
          <span aria-hidden="true">＋</span> Add member
        </Button>
      </div>

      <div className="rounded-card border border-dusk/20 bg-dusk-soft px-4 py-3 text-sm text-ink-soft">
        <p>
          <span className="font-medium text-ink">Adding a kid?</span> Their email has to be the one
          they sign in to Cloudflare Access with — that's how the app knows them. You can add them
          now and leave the email blank until their account exists; they'll show up in chores and on
          the calendar either way.
        </p>
      </div>

      {actionError && <AlertNote message={actionError} />}

      <ConfigTable<FamilyMember>
        items={items}
        error={error}
        onRetry={() => void load()}
        keyOf={(m) => m.id}
        labelOf={(m) => m.name}
        isInactive={(m) => !m.active}
        empty={{
          title: 'Nobody here yet',
          body: 'Add the adults first — an adult with a Cloudflare Access email is what keeps Money and Settings reachable. Kids can come next.',
          actionLabel: 'Add the first member',
          onAction: () => openEditor('new'),
        }}
        renderRow={(member) => (
          <div className="flex min-w-0 items-center gap-3">
            <span
              aria-hidden="true"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-semibold text-white"
              style={{ backgroundColor: member.color }}
            >
              {member.name.slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate font-medium text-ink">{member.name}</span>
                <Pill tone={member.role === 'adult' ? 'sage' : 'honey'}>
                  {member.role === 'adult' ? 'Adult' : 'Child'}
                </Pill>
                {isSelf(member) && <Pill tone="dusk">You</Pill>}
                {!member.active && <Pill>Removed</Pill>}
              </div>
              <p className="truncate text-sm text-ink-soft">
                {member.email ?? <span className="text-ink-faint">No sign-in email yet</span>}
              </p>
            </div>
          </div>
        )}
        renderActions={(member) => (
          <>
            <Button variant="ghost" onClick={() => openEditor(member)}>
              Edit
              <span className="sr-only"> {member.name}</span>
            </Button>
            {member.active && (
              <Button variant="ghost" onClick={() => openSharing(member)}>
                Location
                <span className="sr-only"> sharing for {member.name}</span>
                {member.location_token && (
                  <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-sage" aria-label="on" />
                )}
              </Button>
            )}
            {member.active ? (
              <Button
                variant="ghost"
                disabled={isSelf(member)}
                title={isSelf(member) ? "You can't remove yourself" : undefined}
                onClick={() => openConfirm(member)}
              >
                Remove
                <span className="sr-only"> {member.name}</span>
              </Button>
            ) : (
              <Button variant="secondary" disabled={busy} onClick={() => void setActive(member, true)}>
                Restore
                <span className="sr-only"> {member.name}</span>
              </Button>
            )}
          </>
        )}
      />

      <label className="flex min-h-11 w-fit items-center gap-2 text-sm text-ink-soft">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
          className="h-5 w-5 rounded border-border accent-sage focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
        />
        Show removed members
      </label>

      {editing && (
        <MemberDialog
          member={editing === 'new' ? null : editing}
          isSelf={editing !== 'new' && isSelf(editing)}
          busy={busy}
          error={formError}
          onCancel={closeEditor}
          onSave={(input) => void save(input)}
        />
      )}

      {sharing && (
        <LocationShareDialog
          member={sharing}
          busy={busy}
          error={shareError}
          onGenerate={() => void generateToken(sharing)}
          onStop={() => void stopToken(sharing)}
          onClose={closeSharing}
        />
      )}

      {confirming && (
        <Dialog
          title={`Remove ${confirming.name}?`}
          description={`They lose access to the app right away. Their chores, allowance ledger and history stay exactly where they are, and you can restore them any time.`}
          onClose={closeConfirm}
        >
          <div className="flex flex-col gap-4">
            {confirmError && <AlertNote message={confirmError} focusOnShow />}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="secondary" onClick={closeConfirm}>
                Keep them
              </Button>
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => void setActive(confirming, false)}
              >
                {busy ? 'Removing…' : 'Remove'}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </section>
  );
}

/**
 * Location sharing for one member — the OwnTracks setup. When sharing is on, this
 * shows the person's unique ingest URL to paste into the OwnTracks app on their
 * phone. The URL contains their token, so it's a secret: anyone with it can post
 * that person's location. Regenerate invalidates the old one; Stop sharing clears it.
 */
function LocationShareDialog({
  member,
  busy,
  error,
  onGenerate,
  onStop,
  onClose,
}: {
  member: FamilyMember;
  busy: boolean;
  error: string | null;
  onGenerate: () => void;
  onStop: () => void;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const url = member.location_token
    ? `${window.location.origin}/api/ingest/owntracks/${member.location_token}`
    : null;

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Dialog
      title={`Location sharing — ${member.name}`}
      description="Share location straight from their phone into the app, with no third party in the middle. Uses the free, open-source OwnTracks app."
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        {error && <AlertNote message={error} focusOnShow />}

        {!url ? (
          <>
            <p className="text-sm text-ink-soft">
              Turning this on creates a private link for {member.name}'s phone. Install{' '}
              <span className="font-medium text-ink">OwnTracks</span> on their phone, paste the link
              in, and it quietly reports their location here — nothing leaves your own servers.
            </p>
            <div className="flex justify-end">
              <Button variant="primary" disabled={busy} onClick={onGenerate}>
                {busy ? 'Turning on…' : 'Turn on sharing'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="text-sm font-medium text-ink">Their private link</p>
              <p className="mt-1 text-sm text-ink-soft">
                Paste this into OwnTracks. Keep it secret — anyone with it can post {member.name}'s
                location.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <code className="min-w-0 flex-1 overflow-x-auto rounded-lg border border-border bg-surface-sunk px-3 py-2 font-mono text-xs whitespace-nowrap text-ink select-all">
                  {url}
                </code>
                <Button variant="secondary" onClick={() => void copy()}>
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>

            <ol className="space-y-2 rounded-lg border border-border bg-canvas px-4 py-3 text-sm text-ink-soft">
              <li>1. Install <span className="font-medium text-ink">OwnTracks</span> on their phone (App Store / Play Store).</li>
              <li>2. Open it → Settings → set <span className="font-medium text-ink">Mode</span> to <span className="font-medium text-ink">HTTP</span>.</li>
              <li>3. Paste the link above into the <span className="font-medium text-ink">URL</span> field.</li>
              <li>4. Allow location <span className="font-medium text-ink">Always</span>. That's it — it starts reporting.</li>
            </ol>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Button variant="danger" disabled={busy} onClick={onStop}>
                {busy ? 'Working…' : 'Stop sharing'}
              </Button>
              <Button variant="secondary" disabled={busy} onClick={onGenerate}>
                {busy ? 'Working…' : 'Regenerate link'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

function MemberDialog({
  member,
  isSelf,
  busy,
  error,
  onCancel,
  onSave,
}: {
  member: FamilyMember | null;
  isSelf: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: FamilyMemberInput) => void;
}) {
  const [name, setName] = useState(member?.name ?? '');
  const [role, setRole] = useState<Role>(member?.role ?? 'child');
  const [email, setEmail] = useState(member?.email ?? '');
  const [color, setColor] = useState(member?.color ?? DEFAULT_COLOR);
  const [life360, setLife360] = useState(member?.life360_id ?? '');
  const [errors, setErrors] = useState<{ name?: string; email?: string; color?: string }>({});

  function submit() {
    const trimmedEmail = email.trim().toLowerCase();
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Give them a name.';
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      next.email = 'That does not look like an email address.';
    }
    if (!isHex(color)) next.color = 'Pick a swatch or enter a hex like #5b6ea8.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSave({
      name: name.trim(),
      role,
      email: trimmedEmail || null,
      color: color.trim().toLowerCase(),
      life360_id: life360.trim() || null,
    });
  }

  return (
    <Dialog
      title={member ? `Edit ${member.name}` : 'Add a family member'}
      description={
        member
          ? 'Changes take effect the next time they load the app.'
          : 'Adults see everything. Children see chores, meals, the calendar and the map — never Money.'
      }
      onClose={onCancel}
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {/* Why the Worker said no — inside the modal, where it can actually be seen. */}
        {error && <AlertNote message={error} focusOnShow />}

        <Field label="Name" error={errors.name}>
          {(props) => (
            <TextInput
              {...props}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Bari"
              maxLength={40}
              required
            />
          )}
        </Field>

        <Field
          label="Role"
          hint={
            isSelf
              ? "This is you. You can't change your own role — it would lock you out of Money and Settings."
              : 'Adults get Money, House and Settings. Children get the family screens only.'
          }
        >
          {(props) => (
            <Select
              {...props}
              value={role}
              disabled={isSelf}
              onChange={(e) => setRole(e.target.value === 'adult' ? 'adult' : 'child')}
            >
              <option value="adult">Adult — sees everything</option>
              <option value="child">Child — family screens only</option>
            </Select>
          )}
        </Field>

        <Field
          label="Sign-in email"
          hint="The email on their Cloudflare Access account. Leave blank until they have one — they still show up in chores and the calendar."
          error={errors.email}
        >
          {(props) => (
            <TextInput
              {...props}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="kid@example.com"
              spellCheck={false}
              autoCapitalize="none"
              autoComplete="off"
            />
          )}
        </Field>

        <ColorField
          label="Their color"
          hint="Used for them on chores, the calendar and the map."
          value={color}
          onChange={setColor}
          error={errors.color}
        />

        <Field
          label="Life360 member ID"
          hint="Optional. Links them to a person on the family map once Life360 is connected."
        >
          {(props) => (
            <TextInput
              {...props}
              value={life360}
              onChange={(e) => setLife360(e.target.value)}
              placeholder="Optional"
              spellCheck={false}
              autoCapitalize="none"
            />
          )}
        </Field>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : member ? 'Save changes' : 'Add member'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
